/**
 * 高信頼性録音システム統合クラス
 * 全機能を統合した録音管理システム
 */

import { ChunkedRecorder } from './chunkedRecorder.js'
import { IndexedDBStorage } from './indexedDBStorage.js'
import { ChunkUploader } from './chunkUploader.js'
import { WakeLockManager } from './wakeLockManager.js'

export class ReliableRecorder {
  constructor(options = {}) {
    this.recordingId = null
    this.chunks = []
    this.chunkedRecorder = null
    this.storage = new IndexedDBStorage()
    this.uploader = new ChunkUploader({
      ...options,
      onChunkUploaded: this.handleChunkUploaded.bind(this)
    })
    this.wakeLock = new WakeLockManager()
    
    // 録音状態
    this.isRecording = false
    this.isUploading = false
    this.recordingTime = 0
    this.totalChunks = 0
    this.uploadedChunks = 0
    
    // イベントハンドラー
    this.onStart = options.onStart || (() => {})
    this.onProgress = options.onProgress || (() => {})
    this.onChunk = options.onChunk || (() => {})
    this.onComplete = options.onComplete || (() => {})
    this.onError = options.onError || (() => {})
    // 動画プレビュー表示用（音声では未使用）
    this.onStreamReady = options.onStreamReady || (() => {})

    // 設定
    this.maxRecordingTime = options.maxRecordingTime || 1200000 // 20分
    this.autoUpload = options.autoUpload !== false // デフォルト有効

    // 最大録音時間到達時の通知制御
    this.maxTimeTimerId = null
    this.maxTimeAlertRequested = false
    this.maxTimeAlertShown = false

    // 動画のみ: 録画開始前にprepareVideoPreview()で用意済みのChunkedRecorder（プレビュー確認・向き補正用）
    this._preparedOptions = null
  }

  /**
   * 動画プレビュー準備（録画ボタンを押す前に呼ぶ）。
   * カメラ取得だけを行い、MediaRecorderはまだ起動しない。
   * 戻り値のsessionでプレビュー用streamの向き（sourceInfo）や回転を確認できる。
   * @param {Object} options startRecordingと同じ形（day, type, userId, studentId, submissionId, recordingMode）
   * @param {number} initialRotationDeg
   */
  async prepareVideoPreview(options, initialRotationDeg = 0) {
    const { day, type, userId, learnerId = null } = options
    this.chunkedRecorder = new ChunkedRecorder({
      day,
      type,
      userId,
      learnerId: learnerId ?? null,
      recordingMode: options.recordingMode,
      studentId: options.studentId,
      submissionId: options.submissionId,
      mediaKind: 'video',
      onStreamReady: this.onStreamReady,
      onChunk: this.handleChunk.bind(this),
      onProgress: this.handleProgress.bind(this),
      onError: this.handleError.bind(this),
      onComplete: this.handleRecordingComplete.bind(this)
    })
    const session = await this.chunkedRecorder.prepareVideoPreview(initialRotationDeg)
    this._preparedOptions = { day, type, userId, learnerId }
    return session
  }

  /**
   * プレビュー中の向きを時計回りに90度回転する
   * @returns {number} 回転後の角度（0/90/180/270）。プレビュー未準備なら0
   */
  rotatePreviewClockwise() {
    if (!this.chunkedRecorder?.portraitSession) return 0
    return this.chunkedRecorder.portraitSession.rotateClockwise()
  }

  /**
   * プレビュー確認をキャンセルする（録画開始前のみ有効。カメラを解放する）
   */
  cancelPreview() {
    if (this.chunkedRecorder && !this.isRecording) {
      this.chunkedRecorder.destroy()
      this.chunkedRecorder = null
      this._preparedOptions = null
    }
  }

  /**
   * 録音開始
   */
  async startRecording(options) {
    const { day, type, userId, learnerId = null } = options
    try {
      console.log('🎤 高信頼性録音開始:', { day, type, userId, learnerId })
      
      // 1. IndexedDBの古いデータクリーンアップ（バックグラウンドで実行）
      this.uploader.storage.cleanupOldData(3) // 3日以上前のデータを削除
        .then(deletedCount => {
          if (deletedCount > 0) {
            console.log(`🧹 IndexedDB自動クリーンアップ完了: ${deletedCount}個の古いチャンクを削除`)
          }
        })
        .catch(error => {
          console.warn('⚠️ IndexedDB自動クリーンアップエラー:', error)
        })
      
      // 2. スリープ防止開始
      const wakeLockSuccess = await this.wakeLock.start()
      if (!wakeLockSuccess) {
        console.warn('⚠️ スリープ防止機能が利用できません。録音は不安定になる可能性があります。')
      }
      
      // 2. チャンク分割録音器初期化
      // 動画で事前にprepareVideoPreview()が呼ばれていれば、そのカメラセッションを
      // そのまま使う（プレビューで確認・回転補正した内容と実際の録画内容を一致させるため）
      const canReusePrepared = options.mediaKind === 'video' && this.chunkedRecorder && this._preparedOptions
      if (!canReusePrepared) {
        this.chunkedRecorder = new ChunkedRecorder({
          day,
          type,
          userId,
          learnerId: learnerId ?? null,
          // フィードバック録音用パラメータ
          recordingMode: options.recordingMode,
          studentId: options.studentId,
          submissionId: options.submissionId,
          mediaKind: options.mediaKind, // 'audio'（デフォルト）または 'video'
          onStreamReady: this.onStreamReady,
          onChunk: this.handleChunk.bind(this),
          onProgress: this.handleProgress.bind(this),
          onError: this.handleError.bind(this),
          onComplete: this.handleRecordingComplete.bind(this)
        })
      }
      this._preparedOptions = null

      // 3. アップローダーのエラーハンドリング設定
      // チャンク1個のアップロード失敗（リトライ上限到達）は、そのチャンクが欠けるだけなので
      // 録音自体は止めない（そのチャンクは markChunkAsFailed 済みでスキップされる）。
      // 録音を止めるのは MediaRecorder 自体のエラー（onError: this.handleError.bind(this) 側）のみ。
      this.uploader.onError = (error) => {
        console.error('❌ チャンクのアップロードに失敗しました（録音は継続します）:', error)
      }
      
      // 4. 録音開始
      await this.chunkedRecorder.start()
      
      this.isRecording = true
      this.recordingId = this.chunkedRecorder.recordingId
      this.recordingTime = 0
      this.totalChunks = 0
      this.uploadedChunks = 0
      this.notificationSent = false // 録音開始時にフラグをリセット

      // 最大時間到達の通知フラグをリセット
      this.maxTimeAlertRequested = false
      this.maxTimeAlertShown = false
      if (this.maxTimeTimerId) {
        clearTimeout(this.maxTimeTimerId)
        this.maxTimeTimerId = null
      }
      
      // 録音パラメータを保存
      this.userId = userId
      this.day = day
      this.type = type
      
      // 4. 最大録音時間制限
      this.setupMaxTimeLimit()
      
      // 5. 開始イベント発火
      this.onStart({
        recordingId: this.recordingId,
        day,
        type,
        userId,
        wakeLockStatus: this.wakeLock.getStatus()
      })
      
      console.log('✅ 高信頼性録音開始完了')
      
    } catch (error) {
      console.error('❌ 録音開始エラー:', error)
      this.handleError(error)
      throw error
    }
  }
  
  /**
   * 録音停止（常に Promise を返し、呼び出し側で .catch / await しても安全）
   */
  stopRecording() {
    if (!this.isRecording || !this.chunkedRecorder) {
      return Promise.resolve()
    }

    console.log('⏹️ 高信頼性録音停止')

    // 録音停止したのでタイマーを止める（多重発火防止）
    if (this.maxTimeTimerId) {
      clearTimeout(this.maxTimeTimerId)
      this.maxTimeTimerId = null
    }

    this.isRecording = false
    this.chunkedRecorder.stop()

    // スリープ防止停止
    this.wakeLock.stop()
    return Promise.resolve()
  }
  
  /**
   * チャンク処理
   */
  async handleChunk(chunkData, chunkInfo) {
    try {
      console.log('📦 チャンク処理開始:', chunkInfo)
      
      // 1. IndexedDBに保存
      await this.storage.saveChunk(chunkData, chunkInfo)
      
      // 2. チャンク情報を保存
      this.chunks.push({
        size: chunkData.size,
        timestamp: chunkInfo.timestamp,
        uploaded: false
      })
      
      // 3. 自動アップロードが有効な場合は即座にアップロード
      if (this.autoUpload) {
        await this.uploader.uploadChunk(chunkData, chunkInfo)
        // アップロード完了をマーク（uploadedChunks++はhandleChunkUploadedで実行）
        this.chunks[this.chunks.length - 1].uploaded = true
        
        // アップロード完了をマーク（uploadedChunks++はhandleChunkUploadedで実行）
      }
      
      this.totalChunks++
      
      // 4. チャンクイベント発火
      this.onChunk({
        chunkData,
        chunkInfo,
        totalChunks: this.totalChunks,
        uploadedChunks: this.uploadedChunks
      })
      
    } catch (error) {
      console.error('❌ チャンク処理エラー:', error)
      this.handleError(error)
    }
  }
  
  /**
   * 進捗処理
   */
  handleProgress(progress) {
    this.recordingTime = progress.duration
    
    this.onProgress({
      ...progress,
      isRecording: this.isRecording,
      isUploading: this.uploader.getStatus().activeUploads > 0,
      uploadProgress: this.totalChunks > 0 ? (this.uploadedChunks / this.totalChunks) * 100 : 0
    })
  }
  
  /**
   * 録音完了処理
   */
  async handleRecordingComplete(recordingInfo, chunks) {
    try {
      console.log('✅ 録音完了処理開始:', recordingInfo)
      
      this.isRecording = false
      this.isUploading = true
      
      // 1. スリープ防止停止は既にstopRecording()で実行済み
      
      // 2. 最終チャンクの到着を待機（MediaRecorderのstop完了保証）
      console.log('⏳ 最終チャンク到着待機中...')
      await this.waitForFinalChunk()
      console.log('✅ 最終チャンク到着完了')
      
      // 3. 全チャンクアップロード完了を待機
      if (this.autoUpload) {
        try {
          console.log('⏳ 全チャンクアップロード完了待機中...')
          await this.uploader.processQueue()
          await this.uploader.waitForAllUploads(this.recordingId)
          console.log('✅ 全アップロード完了確認')
          
          // 安全マージン（サーバー側でのファイル確定を待つ）
          await new Promise(resolve => setTimeout(resolve, 2000))
          console.log('✅ 安全マージン待機完了')
          
          // 4. バックエンド処理は最後のチャンクアップロード完了時に自動実行される
          console.log('✅ 最後のチャンクアップロード完了時にバックエンド処理が自動実行されます')
        } catch (error) {
          console.error('❌ アップロード完了待機エラー:', error)
          throw error
        }
      }
      
      // 5. 完了イベント発火（全処理完了後）
      await this.onComplete({
        ...recordingInfo,
        totalChunks: this.totalChunks,
        uploadedChunks: this.uploadedChunks,
        uploadProgress: 100
      })
      
      this.isUploading = false

      // 最大録音時間到達による自動停止の場合だけ、最後にポップアップを出す
      if (this.maxTimeAlertRequested && !this.maxTimeAlertShown) {
        this.maxTimeAlertShown = true
        this.maxTimeAlertRequested = false

        // Vue の状態更新が反映されるまでイベントループに一度返す
        await new Promise(resolve => setTimeout(resolve, 0))
        alert('最大録音時間（20分）に達したため、録音を自動停止しました。')
      }
      
      console.log('✅ 録音完了処理完了')
      
    } catch (error) {
      console.error('❌ 録音完了処理エラー:', error)
      this.handleError(error)
    }
  }
  
  /**
   * 最終チャンクの到着を待機
   */
  async waitForFinalChunk() {
    return new Promise((resolve) => {
      // MediaRecorderのstop後、最後のdataavailableが発生するまで待機
      // 通常は即座に解決するが、稀に遅延することがある
      const maxWaitTime = 3000 // 3秒でタイムアウト
      const checkInterval = 100 // 100ms間隔でチェック
      let waitTime = 0
      
      const checkComplete = () => {
        // ChunkedRecorderがstop状態になっていることを確認
        if (!this.chunkedRecorder || !this.chunkedRecorder.isRecording) {
          resolve()
          return
        }
        
        waitTime += checkInterval
        if (waitTime >= maxWaitTime) {
          console.warn('⚠️ 最終チャンク待機タイムアウト（継続）')
          resolve()
          return
        }
        
        setTimeout(checkComplete, checkInterval)
      }
      
      checkComplete()
    })
  }
  
  /**
   * チャンクアップロード完了処理
   */
  handleChunkUploaded(chunkInfo) {
    this.uploadedChunks++
    console.log('✅ チャンクアップロード完了:', chunkInfo.chunkIndex, `(${this.uploadedChunks}/${this.totalChunks})`)
    
    // 進捗イベントを発火
    this.onProgress({
      recordingTime: this.recordingTime,
      totalChunks: this.totalChunks,
      uploadedChunks: this.uploadedChunks,
      uploadProgress: this.totalChunks > 0 ? (this.uploadedChunks / this.totalChunks) * 100 : 0
    })
  }
  
  /**
   * エラー処理
   */
  handleError(error) {
    console.error('❌ 高信頼性録音エラー:', error)
    console.log('🔍 handleError実行: isRecording =', this.isRecording)
    
    // 1. スリープ防止停止
    this.wakeLock.stop()
    
    // 2. 録音停止（ChunkedRecorderを停止）
    if (this.chunkedRecorder) {
      console.log('🛑 ChunkedRecorder停止')
      this.chunkedRecorder.stop()
      console.log('✅ ChunkedRecorder停止完了')
    }
    
    // 3. 状態リセット
    this.isRecording = false
    this.isUploading = false
    
    console.log('🔍 状態リセット完了: isRecording =', this.isRecording)
    
    const errorMessage = error?.message || (error ? String(error) : '録音中にエラーが発生しました')
    this.onError({
      type: 'recording_error',
      message: errorMessage,
      error: errorMessage,
      recordingId: this.recordingId,
      recordingTime: this.recordingTime,
      totalChunks: this.totalChunks,
      uploadedChunks: this.uploadedChunks
    })

    // 最大録音時間到達で停止した場合、エラーでも最後に通知する
    if (this.maxTimeAlertRequested && !this.maxTimeAlertShown) {
      this.maxTimeAlertShown = true
      this.maxTimeAlertRequested = false
      setTimeout(() => {
        alert('最大録音時間（20分）に達したため録音を自動停止しました。処理中にエラーが発生しました。')
      }, 0)
    }
  }
  
  /**
   * 最大録音時間制限設定
   */
  setupMaxTimeLimit() {
    if (this.maxTimeTimerId) {
      clearTimeout(this.maxTimeTimerId)
      this.maxTimeTimerId = null
    }

    this.maxTimeTimerId = setTimeout(() => {
      if (this.isRecording) {
        console.log('⏰ 最大録音時間到達、自動停止')
        // 通知は handleRecordingComplete の最後にまとめて行う
        this.maxTimeAlertRequested = true
        this.stopRecording()
      }
    }, this.maxRecordingTime)
  }
  
  /**
   * 未アップロードチャンクの再処理
   */
  async retryFailedUploads() {
    try {
      console.log('🔄 失敗チャンク再処理開始')
      await this.uploader.retryFailedUploads()
    } catch (error) {
      console.error('❌ 失敗チャンク再処理エラー:', error)
    }
  }
  
  /**
   * 録音データクリーンアップ
   */
  async cleanup(recordingId) {
    try {
      console.log('🗑️ 録音データクリーンアップ開始:', recordingId)
      await this.storage.cleanupRecording(recordingId)
    } catch (error) {
      console.error('❌ クリーンアップエラー:', error)
    }
  }
  
  /**
   * 状態取得
   */
  getStatus() {
    return {
      isRecording: this.isRecording,
      isUploading: this.isUploading,
      recordingId: this.recordingId,
      recordingTime: this.recordingTime,
      totalChunks: this.totalChunks,
      uploadedChunks: this.uploadedChunks,
      uploadProgress: this.totalChunks > 0 ? (this.uploadedChunks / this.totalChunks) * 100 : 0,
      wakeLockStatus: this.wakeLock.getStatus(),
      uploaderStatus: this.uploader.getStatus()
    }
  }
  

  /**
   * リソース解放
   */
  destroy() {
    if (this.chunkedRecorder) {
      this.chunkedRecorder.destroy()
    }
    
    this.wakeLock.stop()

    if (this.maxTimeTimerId) {
      clearTimeout(this.maxTimeTimerId)
      this.maxTimeTimerId = null
    }
    
    this.isRecording = false
    this.isUploading = false
    this.recordingId = null
    this.recordingTime = 0
    this.totalChunks = 0
    this.uploadedChunks = 0

    this.maxTimeAlertRequested = false
    this.maxTimeAlertShown = false
  }
}
